// src/context/ChecklistDefaultsContext.js
import React, { createContext, useContext, useMemo } from "react";
import { useSeasons } from "./SeasonContext";
import { useAlerts } from "./AlertsContext";

const ChecklistDefaultsContext = createContext(null);

export const ChecklistDefaultsProvider = ({ children }) => {
  const { currentSeasons } = useSeasons();
  const { flags, flagsReady } = useAlerts();

  const value = useMemo(() => {
    // 1. Seasonal base hazard: single source of truth
    let seasonalHazard = "Routine";

    if (currentSeasons.some((s) => s.id === "winterWeather")) {
      seasonalHazard = "Winter Weather";
    } else if (currentSeasons.some((s) => s.id === "hurricane")) {
      seasonalHazard = "Hurricane";
    } else if (currentSeasons.some((s) => s.id === "wildfire")) {
      seasonalHazard = "Wildfire";
    } else if (currentSeasons.some((s) => s.id === "severeStorms")) {
      seasonalHazard = "Severe Storms";
    } else if (currentSeasons.some((s) => s.id === "flooding")) {
      seasonalHazard = "Flooding";
    } else if (currentSeasons.some((s) => s.id === "surfRip")) {
      seasonalHazard = "Surf & Rip Currents";
    }

    // Always start from the seasonal hazard each time
    let defaultHazard = seasonalHazard;
    let defaultAlertType = "Seasonal";

    // 2. Before flags are ready, stay purely seasonal
    if (!flagsReady) {
      console.log("[ChecklistDefaults] (seasonal only, flags not ready) ->", {
        seasonalHazard,
        defaultHazard,
        defaultAlertType,
        flags,
        flagsReady,
      });

      return { defaultHazard, defaultAlertType };
    }

    console.log("[ChecklistDefaults] (overlay with flags) input ->", {
      seasonalHazard,
      flags,
      flagsReady,
    });

    // 3. Overlay: refine alertType within the seasonal context

    // Winter: Warning / Watch if present, else explicitly Seasonal
    if (seasonalHazard === "Winter Weather") {
      if (flags?.hasWinterWarning) {
        defaultAlertType = "Warning";
      } else if (flags?.hasWinterWatch) {
        defaultAlertType = "Watch";
      } else {
        defaultAlertType = "Seasonal";
      }
    }

    // Wildfire: Burn Ban if present, otherwise Seasonal
    if (seasonalHazard === "Wildfire") {
      if (flags?.hasBurnBan) {
        defaultAlertType = "Burn Ban";
      } else if (flags?.hasFireWeather) {
        // Could later become "Elevated", but keep Seasonal for now
        defaultAlertType = "Seasonal";
      } else {
        defaultAlertType = "Seasonal";
      }
    }

    // Severe storms: Warning only when severe flag is set
    if (seasonalHazard === "Severe Storms") {
      if (flags?.hasSevereStorm) {
        defaultAlertType = "Warning";
      } else {
        defaultAlertType = "Seasonal";
      }
    }

    // Flooding: Warning only when flooding flag is set
    if (seasonalHazard === "Flooding") {
      if (flags?.hasFlooding) {
        defaultAlertType = "Warning";
      } else {
        defaultAlertType = "Seasonal";
      }
    }

    // Surf & rip currents: Warning only when surf hazard is set
    if (seasonalHazard === "Surf & Rip Currents") {
      if (flags?.hasSurfHazard) {
        defaultAlertType = "Warning";
      } else {
        defaultAlertType = "Seasonal";
      }
    }

    // Extreme cold as cross-cutting within winter
    if (flags?.hasExtremeCold && seasonalHazard === "Winter Weather") {
      console.log("[ChecklistDefaults] Extreme cold within winter season");
    }

    console.log("[ChecklistDefaults] (final defaults) ->", {
      seasonalHazard,
      defaultHazard,
      defaultAlertType,
      flags,
      flagsReady,
    });

    return { defaultHazard, defaultAlertType };
  }, [
    currentSeasons,
    flagsReady,
    flags?.hasWinterStorm,
    flags?.hasWinterWatch,
    flags?.hasWinterWarning,
    flags?.hasBurnBan,
    flags?.hasFireWeather,
    flags?.hasSevereStorm,
    flags?.hasFlooding,
    flags?.hasSurfHazard,
    flags?.hasExtremeCold,
  ]);

  return (
    <ChecklistDefaultsContext.Provider value={value}>
      {children}
    </ChecklistDefaultsContext.Provider>
  );
};

export const useChecklistDefaults = () => {
  const ctx = useContext(ChecklistDefaultsContext);
  if (!ctx) {
    throw new Error(
      "useChecklistDefaults must be used within ChecklistDefaultsProvider"
    );
  }
  return ctx;
};
